import { getCartLines, getMaticCount } from "@/cart/displayCart";
import GRAPHQL_CLIENT from "@/graphql/client";
import type { Cart } from "@/graphql/storefront/generated";
import { isMaticCare, isStandardBagPass } from "@/utils/ShopifyHelpers";
import { PET_BRUSH_ROLL_VARIANT_ID } from "@/consts/shopify";

// Adding a Matic nudges its add-ons up by the same amount so coverage keeps
// pace with the robots, but never past one add-on per Matic. This is a
// one-time bump on the increment, not a lock: shoppers can still lower Matic
// Care, Pet Brush Roll, or bag passes afterward, and an add-on already at or
// above the Matic count is left untouched. Referral rewards sync separately.
export const raiseAddOnsWithMatics = async (
  cart: Cart,
  addedMatics: number,
): Promise<Cart> => {
  if (addedMatics <= 0) {
    return cart;
  }

  const maticCount = getMaticCount(cart);
  const lineUpdates: Array<{ id: string; quantity: number }> = [];
  for (const line of getCartLines(cart)) {
    if (
      !isMaticCare(line) &&
      !isStandardBagPass(line) &&
      line.merchandise.id !== PET_BRUSH_ROLL_VARIANT_ID
    ) {
      continue;
    }
    const bump = Math.min(addedMatics, maticCount - line.quantity);
    if (bump > 0) {
      lineUpdates.push({ id: line.id, quantity: line.quantity + bump });
    }
  }

  if (lineUpdates.length === 0) {
    return cart;
  }

  const { cartLinesUpdate } = await GRAPHQL_CLIENT.UpdateCartLine({
    cartId: cart.id,
    lines: lineUpdates,
  });
  const nextCart = cartLinesUpdate?.cart as Cart | undefined;
  if (!nextCart) {
    throw new Error("raiseAddOnsWithMatics: raising add-on lines failed");
  }

  return nextCart;
};
